"use client";

import type { FC, MouseEvent, ReactNode } from "react";
import { createContext } from "react";

import type { UIAction } from "../types/UIAction";

export type ThemeScope =
  | "maximumMortgage"
  | "orientation"
  | "financialProfile"
  | "interestRates"
  | "advice"
  | "documentsOutgoing"
  | "documentsIncoming"
  | "assessment"
  | "advisor"
  | "mediation";

export type ThemeSlotKey =
  | "appointmentBooking"
  | "authWrapper"
  | "scopeVisual"
  | "hubStepCard"
  | "hubHouseAnalysisCard"
  | "hubHeading"
  | "pageHeading"
  | "maximumMortgageResultBanner"
  | "maximumMortgageResultComparison";

export type MaximumMortgageResultBannerCTA = {
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
  tracking?: string;
  href?: string;
  value?: string;
};

export type ThemeSlotPropsMap = {
  maximumMortgageResultComparison: {
    yourSituationCTA: ReactNode;
    neededSituationCTA: ReactNode;
    yourSituationValue: string | number;
    yourSituationMonthlyValue: string | number;
    neededSituationValue: string | number;
    neededSituationMonthlyValue: string | number;
    yourSituationDescription: string;
    neededSituationTitle?: string;
    neededSituationDescription: string;
    hasPartner?: boolean;
    hideNeededSituation?: boolean;
  };
  maximumMortgageResultBanner: {
    resultType: string;
    hasPartner?: boolean;
    advisorAction?: MaximumMortgageResultBannerCTA;
    continueAction?: MaximumMortgageResultBannerCTA;
    thisSubjectKey: string;
    theSubjectKey: string;
  };
  pageHeading: {
    scope: ThemeScope;
    title?: string;
    description?: string;
  };
  /**
   * Per-scope visual (icon/illustration) shared by hub cards and page
   * headings. A theme registers one `scopeVisual` and it is used wherever a
   * scope icon is rendered. `size` is the requested rendered size in px;
   * the default badge/illustration keep their own sizing.
   */
  scopeVisual: { scope: ThemeScope; size?: number; alt?: string };
  hubStepCard: {
    stepNumber: number;
    scope?: ThemeScope;
    title: string;
    description: string;
    completed?: boolean;
    action?: UIAction;
    disabled?: boolean;
    children?: ReactNode;
  };
  hubHouseAnalysisCard: {
    title?: string;
    description?: string;
    action?: UIAction;
    disabled?: boolean;
    propertyImage?: string;
    propertyPrice?: number;
    propertyAddress?: string;
    propertyAction?: UIAction;
    children?: ReactNode;
  };
};

type DefaultSlotProps = { children?: ReactNode };

export type ThemeSlotMap = Partial<{
  [K in ThemeSlotKey]: K extends keyof ThemeSlotPropsMap
    ? FC<ThemeSlotPropsMap[K]>
    : FC<DefaultSlotProps>;
}>;

export const ThemeSlotContext = createContext<ThemeSlotMap>({});
